import React, { useState } from 'react';
import { KeyIcon, ClipboardIcon, CheckIcon, ArrowPathIcon } from '@heroicons/react/24/outline';
import CodeBlock from './CodeBlock';
import toast from 'react-hot-toast';

export default function ApiOnboarding({ apiKey }) {
  const [copied, setCopied] = useState(false);
  
  const handleCopy = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      toast.success('Copied to clipboard!');
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      toast.error('Failed to copy to clipboard');
    }
  };

  const quickStartCode = `# Install using pip
pip install requests

# Python example
import requests

def remove_background(image_path):
    url = 'https://remuvr-api.onrender.com/remove'
    headers = {
        'Authorization': 'Bearer ${apiKey}'
    }
    
    with open(image_path, 'rb') as image:
        files = {'file': image}
        response = requests.post(url, headers=headers, files=files)
    
    if response.status_code == 200:
        with open('result.png', 'wb') as f:
            f.write(response.content)
        return 'result.png'
    else:
        raise Exception(f'Error: {response.text}')`;

  return (
    <div className="bg-[#1E293B] p-8 rounded-xl border border-[#334155] space-y-8">
      {/* Welcome Section */}
      <div className="text-center">
        <div className="mx-auto w-16 h-16 bg-[#2563EB]/10 rounded-full flex items-center justify-center mb-4">
          <KeyIcon className="w-8 h-8 text-[#2563EB]" />
        </div>
        <h2 className="text-2xl font-bold text-white mb-2">Welcome to Remuvr API</h2>
        <p className="text-[#94A3B8]">You're all set! Here's everything you need to get started.</p>
      </div>

      {/* API Key Section */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-white">Your API Key</h3>
          <button
            onClick={() => window.location.reload()}
            className="flex items-center space-x-2 text-[#94A3B8] hover:text-white transition-colors duration-300"
          >
            <ArrowPathIcon className="w-4 h-4" />
            <span className="text-sm">Refresh</span>
          </button>
        </div>
        <div className="relative">
          <div className="bg-[#0F172A] p-4 rounded-lg font-mono text-sm break-all text-[#94A3B8] pr-12">
            {apiKey}
          </div>
          <button
            onClick={() => handleCopy(apiKey)}
            className="absolute right-2 top-1/2 -translate-y-1/2 p-2 hover:bg-[#1E293B] rounded-lg transition-colors duration-300"
          >
            {copied ? (
              <CheckIcon className="w-5 h-5 text-green-500" />
            ) : (
              <ClipboardIcon className="w-5 h-5 text-[#94A3B8]" />
            )}
          </button>
        </div>
        <p className="text-sm text-[#94A3B8]">
          Keep this key secure and never share it publicly. You can generate a new key anytime from your dashboard.
        </p>
      </div>

      {/* Quick Start Section */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-white">Quick Start</h3>
        <CodeBlock code={quickStartCode} language="python" />
      </div>

      {/* Next Steps */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-white">Next Steps</h3>
        <ul className="space-y-3">
          <li className="flex items-start">
            <div className="flex-shrink-0 h-6 w-6 rounded-full bg-[#2563EB]/10 flex items-center justify-center mr-3 mt-0.5">
              <span className="text-[#2563EB] text-sm">1</span>
            </div>
            <p className="text-[#94A3B8]">
              Read our <a href="/api-docs" className="text-[#2563EB] hover:text-[#1D4ED8]">API documentation</a> for detailed endpoint references and examples
            </p>
          </li>
          <li className="flex items-start">
            <div className="flex-shrink-0 h-6 w-6 rounded-full bg-[#2563EB]/10 flex items-center justify-center mr-3 mt-0.5">
              <span className="text-[#2563EB] text-sm">2</span>
            </div>
            <p className="text-[#94A3B8]">
              Try our example code above with a test image
            </p>
          </li>
          <li className="flex items-start">
            <div className="flex-shrink-0 h-6 w-6 rounded-full bg-[#2563EB]/10 flex items-center justify-center mr-3 mt-0.5">
              <span className="text-[#2563EB] text-sm">3</span>
            </div>
            <p className="text-[#94A3B8]">
              Monitor your API usage in the <a href="/dashboard" className="text-[#2563EB] hover:text-[#1D4ED8]">dashboard</a>
            </p>
          </li>
        </ul>
      </div>

      {/* Help Section */}
      <div className="text-center pt-4">
        <p className="text-[#94A3B8]">
          Need help? Check out our <a href="/api-docs#faq" className="text-[#2563EB] hover:text-[#1D4ED8]">FAQ</a> or{' '}
          <a href="/contact" className="text-[#2563EB] hover:text-[#1D4ED8]">contact support</a>
        </p>
      </div>
    </div>
  );
}
